import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import MessagingAPIService, { MessageQuery } from "messaging/_api/messaging-api-service";
import Message from "messaging/message";
import Session from "users/__session/session";
import Comment from "work/proposals/comments/comment";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

type CommentStoreState = {
  entries: Record<string, Comment[]>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
};

type PublishCommentsArgs = {
  session: Session;
  isAutoGenerated?: boolean;
  proposalId?: Guid;
};

type UpdateAutoGeneratedCommentsArgs = {
  proposalId: Guid;
  comments: Comment[];
};

type RetargetPendingCommentsArgs = {
  fromProposalId: Guid;
  toProposalId: Guid;
  isAutoGenerated?: boolean;
};

type PublishTargetComment = {
  published?: unknown;
  isAutoGenerated: boolean;
  thread: {
    proposalId: {
      isEqualTo(other: Guid | null | undefined): boolean;
    };
  };
};

type AutoCommentTarget = {
  content: string;
  published?: unknown;
  isAutoGenerated: boolean;
  thread: CommentThreadTarget;
};

type CommentThreadTarget = {
  proposalId: {
    value: string;
  };
  field: {
    cacheKey: string;
  };
  isExternal?: boolean;
};

const initialState: CommentStoreState = {
  entries: {},
  loading: {},
  error: {},
};

const selectCommentEntries = (state: RootState) => state.comments.entries;

const pendingCommentsCountByFieldSelectorCache = new Map<string, (state: RootState) => number>();
const pendingCommentsCountByCategorySelectorCache = new Map<string, (state: RootState) => number>();

function countMatchingComments(comments: Comment[] | undefined, predicate: (comment: Comment) => boolean): number {
  if (!comments?.length) {
    return 0;
  }

  let count = 0;
  for (const comment of comments) {
    if (predicate(comment)) {
      count += 1;
    }
  }

  return count;
}

function countMatchingCommentsInEntries(
  entries: Record<string, Comment[]>,
  predicate: (comment: Comment) => boolean,
  relevantKeys?: string[],
): number {
  const entryKeys = relevantKeys ?? Object.keys(entries);
  let count = 0;

  for (const entryKey of entryKeys) {
    count += countMatchingComments(entries[entryKey], predicate);
  }

  return count;
}

function matchesPublishTarget(comment: PublishTargetComment, args: PublishCommentsArgs): boolean {
  return Boolean(
    !comment.published &&
      (args.isAutoGenerated === undefined || comment.isAutoGenerated === args.isAutoGenerated) &&
      (!args.proposalId || comment.thread.proposalId.isEqualTo(args.proposalId)),
  );
}

function normalizeAutoCommentContent(content: string): string {
  return content.replace(/\s+/g, " ").trim();
}

function matchesAutoCommentThread(existingThread: CommentThreadTarget, autoCommentThread: CommentThreadTarget): boolean {
  return Boolean(
    existingThread.proposalId.value === autoCommentThread.proposalId.value &&
      existingThread.field.cacheKey === autoCommentThread.field.cacheKey &&
      existingThread.isExternal === autoCommentThread.isExternal,
  );
}

function matchesPublishedAutoComment(existingComment: AutoCommentTarget, autoComment: Comment): boolean {
  return Boolean(
    existingComment.isAutoGenerated &&
      existingComment.published &&
      normalizeAutoCommentContent(existingComment.content) === normalizeAutoCommentContent(autoComment.content) &&
      matchesAutoCommentThread(existingComment.thread, autoComment.thread),
  );
}

export const populateThreadComments = createAsyncThunk(
  "comments/getCommentsByThread",
  async (
    {
      session,
      thread,
      query,
    }: {
      session: Session;
      thread: CommentThread;
      query: MessageQuery;
    },
    thunkAPI,
  ) => {
    try {
      const apiService = new MessagingAPIService(session);
      const commentMessages = await apiService.getMessagesByForum(
        thread.toForum({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
        query,
      );
      return commentMessages.data.map((message: Message) => Comment.fromMessage(message));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const saveComments = createAsyncThunk(
  "comments/saveComments",
  async (
    {
      session,
    }: {
      session: Session;
    },
    thunkAPI,
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;

      const unsavedComments: Comment[] = Object.values(state.comments.entries).flatMap((comments) =>
        comments.filter(
          (comment) =>
            !comment.published &&
            !comment.isAutoGenerated &&
            (comment.markedForPublish ||
              comment.markedForCreation ||
              comment.markedForEdit ||
              comment.markedForReadReceipt ||
              comment.markedForUnread),
        ),
      );
      if (!unsavedComments.length) {
        return [];
      }

      const apiService = new MessagingAPIService(session);
      const messagesToSave = unsavedComments.map((comment) =>
        comment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
      );
      const messages = await apiService.createBulkMessages(messagesToSave);
      return messages.map(Comment.fromMessage);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const publishComments = createAsyncThunk(
  "comments/publishComments",
  async (
    { session, isAutoGenerated, proposalId }: PublishCommentsArgs,
    thunkAPI,
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;

      const pendingComments: Comment[] = Object.values(state.comments.entries)
        .flatMap((comments) =>
          comments.filter((comment) => matchesPublishTarget(comment, { session, isAutoGenerated, proposalId })),
        )
        .map((comment) => comment.markForPublish());

      if (!pendingComments.length) {
        return [];
      }

      const apiService = new MessagingAPIService(session);
      const messagesToSave = pendingComments.map((comment) =>
        comment.toMessage({
          isViewingAsVendor: session.context?.viewingAsVendor ?? false,
        }),
      );
      const messages = await apiService.createBulkMessages(messagesToSave);
      return messages.map(Comment.fromMessage);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    addComment: (state, action: PayloadAction<Comment>) => {
      const fieldKey = action.payload.thread.field?.cacheKey;
      const proposalId = action.payload.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      const existingThreadComments =
        state.entries[commentFieldKey]?.filter((existingComment) => !existingComment.isEqualTo(action.payload)) ?? [];

      existingThreadComments.push(action.payload);
      state.entries[commentFieldKey] = existingThreadComments;
    },
    removeCommentById: (state, action: PayloadAction<Guid>) => {
      for (const fieldCommentsKey of Object.keys(state.entries)) {
        state.entries[fieldCommentsKey] = state.entries[fieldCommentsKey]?.filter(
          (comment) => !comment.id?.isEqualTo(action.payload),
        );
      }
    },
    removeComment: (state, action: PayloadAction<Comment>) => {
      const fieldKey = action.payload.thread.field?.cacheKey;
      const proposalId = action.payload.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.entries[commentFieldKey] = state.entries[commentFieldKey]?.filter(
        (comment) => !comment.isEqualTo(action.payload),
      );
    },

    clearUnsavedCommentsByProposalId(state, action: PayloadAction<Guid>) {
      for (const fieldCommentsKey of Object.keys(state.entries)) {
        state.entries[fieldCommentsKey] = state.entries[fieldCommentsKey]?.filter(
          (comment) => !(comment.thread.proposalId.isEqualTo(action.payload) && !comment.created),
        );
      }
    },
    clearAutoGeneratedCommentsByProposalId(state, action: PayloadAction<Guid>) {
      for (const fieldCommentsKey of Object.keys(state.entries)) {
        state.entries[fieldCommentsKey] = state.entries[fieldCommentsKey]?.filter(
          (comment) =>
            !(comment.thread.proposalId.isEqualTo(action.payload) && comment.isAutoGenerated && !comment.created),
        );
      }
    },
    updateAutoGeneratedComments: (state, action: PayloadAction<UpdateAutoGeneratedCommentsArgs>) => {
      const { proposalId, comments: autoGeneratedComments } = action.payload;
      const existingCommentsFieldKeys: string[] = _.uniq(
        autoGeneratedComments.map((comment) => `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}`),
      );

      for (const commentsFieldKey of Object.keys(state.entries)) {
        const currentComments = state.entries[commentsFieldKey] ?? [];
        state.entries[commentsFieldKey] = currentComments.filter(
          (comment) => !(comment.thread.proposalId.isEqualTo(proposalId) && comment.isAutoGenerated && !comment.created),
        );
      }

      for (const commentsFieldKey of existingCommentsFieldKeys) {
        const newComments = state.entries[commentsFieldKey] ?? [];
        const threadAutoComments = autoGeneratedComments.filter(
          (comment) => `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}` === commentsFieldKey,
        );
        for (const comment of threadAutoComments) {
          if (newComments.some((existingComment) => matchesPublishedAutoComment(existingComment, comment))) {
            continue;
          }
          newComments.push(comment);
        }
        state.entries[commentsFieldKey] = newComments;
      }
    },
    retargetPendingCommentsToProposal: (state, action: PayloadAction<RetargetPendingCommentsArgs>) => {
      const { fromProposalId, toProposalId, isAutoGenerated } = action.payload;
      const retargetedCommentsByFieldKey: Record<string, Comment[]> = {};

      for (const commentsFieldKey of Object.keys(state.entries)) {
        const retainedComments: Comment[] = [];

        for (const comment of state.entries[commentsFieldKey] ?? []) {
          const sourceComment = comment as unknown as Comment;

          const sourceProposalId = sourceComment.thread?.proposalId;
          const sourceField = sourceComment.thread?.field;

          if (
            !sourceComment.isPending ||
            !sourceProposalId?.isEqualTo(fromProposalId) ||
            (isAutoGenerated !== undefined && sourceComment.isAutoGenerated !== isAutoGenerated) ||
            !sourceField?.cacheKey
          ) {
            retainedComments.push(sourceComment);
            continue;
          }

          const retargetedThread = new CommentThread({
            proposalId: toProposalId,
            field: sourceField,
            subscribers: sourceComment.thread.subscribers,
            isExternal: sourceComment.thread.isExternal,
          });
          const retargetedComment = sourceComment.with({
            id: Guid.generate(),
            thread: retargetedThread,
            created: undefined,
            published: undefined,
            updated: undefined,
            markedForCreation: true,
            markedForPublish: sourceComment.markedForPublish,
            markedForEdit: false,
            markedForReadReceipt: undefined,
            markedForUnread: undefined,
            markedForDeletion: false,
            deleted: undefined,
          });
          const retargetedFieldKey = `${toProposalId.value}-${sourceField.cacheKey}`;
          retargetedCommentsByFieldKey[retargetedFieldKey] = [
            ...(retargetedCommentsByFieldKey[retargetedFieldKey] ?? []),
            retargetedComment,
          ];
        }

        state.entries[commentsFieldKey] = retainedComments;
      }

      for (const [commentsFieldKey, retargetedComments] of Object.entries(retargetedCommentsByFieldKey)) {
        state.entries[commentsFieldKey] = [...(state.entries[commentsFieldKey] ?? []), ...retargetedComments];
      }
    },
    updateThreadSubscribers: (state, action: PayloadAction<{ proposal: Proposal; session: Session }>) => {
      const { proposal, session } = action.payload;

      let subscribers;
      try {
        subscribers = proposal.getCommentAudienceAndSubscribers(false, session).subscribers;
      } catch {
        console.warn("User is not associated with the proposal as a reviewer or negotiator");
        return;
      }

      let proposalComments: Comment[] = [];
      for (const commentsFieldKey of Object.keys(state.entries)) {
        const currentComments =
          state.entries[commentsFieldKey]
            .filter((comment) => comment.thread.proposalId.isEqualTo(proposal.id) && comment.isPending)
            .map((c) => c as Comment) ?? [];
        proposalComments = proposalComments.concat(currentComments);
      }

      proposalComments.forEach((comment) => {
        const fieldKey = `${comment.thread.proposalId.value}-${comment.thread.field?.cacheKey}`;
        comment.thread.subscribers = subscribers;
        const newComments = state.entries[fieldKey]?.filter((c) => !c.id?.isEqualTo(comment.id)) ?? [];
        newComments.push(comment);
        state.entries[fieldKey] = newComments;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateThreadComments.pending, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = true;
      state.error[commentFieldKey] = null;
    });
    builder.addCase(populateThreadComments.fulfilled, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = false;
      let existingThreadComments = state.entries[commentFieldKey] || [];

      for (const comment of action.payload) {
        if (existingThreadComments.some((existingComment) => existingComment.id?.isEqualTo(comment.id))) {
          existingThreadComments = existingThreadComments.filter(
            (existingComment) => !existingComment.id?.isEqualTo(comment.id),
          );
        }
        existingThreadComments.push(comment);
      }

      state.entries[commentFieldKey] = existingThreadComments;
    });
    builder.addCase(populateThreadComments.rejected, (state, action) => {
      const fieldKey = action.meta.arg.thread.field.cacheKey;
      const proposalId = action.meta.arg.thread.proposalId;
      if (!fieldKey || !proposalId) {
        return;
      }
      const commentFieldKey = `${proposalId.value}-${fieldKey}`;

      state.loading[commentFieldKey] = false;
      state.error[commentFieldKey] = action.error;
    });
    builder.addCase(saveComments.pending, (state) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => comment.isPending && !comment.isAutoGenerated)) {
          state.loading[commentFieldKey] = true;
          state.error[commentFieldKey] = null;
        }
      }
    });
    builder.addCase(saveComments.fulfilled, (state, action) => {
      for (const comment of action.payload) {
        const fieldKey = comment.thread.field.cacheKey;
        const proposalId = comment.thread.proposalId;
        if (!fieldKey || !proposalId) {
          continue;
        }
        const commentFieldKey = `${proposalId.value}-${fieldKey}`;

        let existingThreadComments = state.entries[`${proposalId.value}-${fieldKey}`] || [];
        existingThreadComments = existingThreadComments.filter(
          (existingComment) => !existingComment.id?.isEqualTo(comment.id),
        );
        existingThreadComments.push(comment);
        state.entries[commentFieldKey] = existingThreadComments;
        state.loading[commentFieldKey] = false;
        state.error[commentFieldKey] = null;
      }
    });
    builder.addCase(saveComments.rejected, (state, action) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => comment.isPending)) {
          state.loading[commentFieldKey] = false;
          state.error[commentFieldKey] = action.error;
        }
      }
    });
    builder.addCase(publishComments.pending, (state, action) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => matchesPublishTarget(comment, action.meta.arg))) {
          state.loading[commentFieldKey] = true;
          state.error[commentFieldKey] = null;
        }
      }
    });
    builder.addCase(publishComments.fulfilled, (state, action) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (!comments.some((comment) => matchesPublishTarget(comment, action.meta.arg))) {
          continue;
        }

        state.entries[commentFieldKey] = comments.filter((comment) => !matchesPublishTarget(comment, action.meta.arg));
        state.loading[commentFieldKey] = false;
        state.error[commentFieldKey] = null;
      }

      for (const comment of action.payload) {
        const fieldKey = comment.thread.field?.cacheKey;
        const proposalId = comment.thread.proposalId;
        if (!fieldKey || !proposalId) {
          continue;
        }

        const commentFieldKey = `${proposalId.value}-${fieldKey}`;
        const existingThreadComments = state.entries[commentFieldKey] ?? [];
        state.entries[commentFieldKey] = existingThreadComments.filter(
          (existingComment) => !existingComment.isEqualTo(comment),
        );
        state.entries[commentFieldKey].push(comment);
        state.loading[commentFieldKey] = false;
        state.error[commentFieldKey] = null;
      }
    });
    builder.addCase(publishComments.rejected, (state, action) => {
      for (const [commentFieldKey, comments] of Object.entries(state.entries)) {
        if (comments.some((comment) => matchesPublishTarget(comment, action.meta.arg))) {
          state.loading[commentFieldKey] = false;
          state.error[commentFieldKey] = action.error;
        }
      }
    });
  },
});

export const {
  addComment,
  removeCommentById,
  removeComment,
  clearUnsavedCommentsByProposalId,
  clearAutoGeneratedCommentsByProposalId,
  updateAutoGeneratedComments,
  retargetPendingCommentsToProposal,
  updateThreadSubscribers,
} = commentsSlice.actions;

export const getCommentsByField =
  (proposalId?: Guid | null, field?: ProposalField) =>
  (state: RootState): Comment[] | undefined => {
    if (!proposalId || !field?.cacheKey) return [];
    return state.comments.entries[`${proposalId.value}-${field?.cacheKey}`] ?? undefined;
  };
export const getPendingCommentsCountByField = (field?: ProposalField, proposalId?: Guid | null) => {
  if (!field?.cacheKey) {
    return () => 0;
  }

  const selectorCacheKey = `${proposalId?.value ?? "all"}-${field.cacheKey}`;
  const cachedSelector = pendingCommentsCountByFieldSelectorCache.get(selectorCacheKey);
  if (cachedSelector) {
    return cachedSelector;
  }

  const selector = createSelector([selectCommentEntries], (entries): number => {
    if (proposalId) {
      return countMatchingComments(entries[`${proposalId.value}-${field.cacheKey}`], (comment) => comment.isPending);
    }

    return countMatchingCommentsInEntries(entries, (comment) => comment.isPending && comment.thread.field.isEqualTo(field));
  });

  pendingCommentsCountByFieldSelectorCache.set(selectorCacheKey, selector);
  return selector;
};
export const getPendingCommentsCountByCategory = (category?: ProposalField["category"], proposalId?: Guid | null) => {
  if (!category) {
    return () => 0;
  }

  const selectorCacheKey = `${proposalId?.value ?? "all"}-${category}`;
  const cachedSelector = pendingCommentsCountByCategorySelectorCache.get(selectorCacheKey);
  if (cachedSelector) {
    return cachedSelector;
  }

  const selector = createSelector([selectCommentEntries], (entries): number => {
    const proposalEntryKeys = proposalId
      ? Object.keys(entries).filter((entryKey) => entryKey.startsWith(`${proposalId.value}-`))
      : undefined;

    return countMatchingCommentsInEntries(
      entries,
      (comment) =>
        comment.isPending &&
        comment.thread.field.category === category &&
        comment.thread.field.name !== ProposalField.General.name &&
        (!proposalId || comment.thread.proposalId.isEqualTo(proposalId)),
      proposalEntryKeys,
    );
  });

  pendingCommentsCountByCategorySelectorCache.set(selectorCacheKey, selector);
  return selector;
};
export const getIsLoadingComments = (proposalId?: Guid | null, field?: ProposalField) => (state: RootState) => {
  if (!proposalId || !field?.cacheKey) return false;
  return state.comments.loading[`${proposalId.value}-${field?.cacheKey}`] ?? false;
};
export const getArePendingComments = (proposal?: Proposal | null) => (state: RootState) => {
  if (!proposal?.id?.value) return false;
  return Object.values(state.comments?.entries)
    .flat()
    .some((comment) => comment.thread.proposalId.isEqualTo(proposal.id) && comment.isPending);
};
export const getAreUnsavedComments = (proposal?: Proposal | null) => (state: RootState) => {
  if (!proposal?.id?.value) return false;
  return Object.values(state.comments?.entries)
    .flat()
    .some(
      (comment) => comment.thread.proposalId.isEqualTo(proposal.id) && !comment.isAutoGenerated && comment.isPending,
    );
};

export default commentsSlice;
